import { unlockTableVoice } from '../services/voiceAccessService';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spade, Crown } from 'lucide-react';
import { auth } from '../firebase';
import gameApiService from '../services/gameApiService';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Input,
  Button,
  Switch,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Tooltip,
  Icon,
  Badge,
  Divider,
  useToast,
  Collapse,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Spinner,
} from '@chakra-ui/react';
import { 
  InfoIcon, 
  LockIcon, 
  UnlockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
  CheckIcon,
} from '@chakra-ui/icons';
import enhancedTableService from '../services/enhancedTableService';
import voiceChatService from '../services/voiceChatService';
import walletService from '../services/walletService';
import geoService from '../services/geoService'; //  NEW: Import geoService

import sessionService, { GameSession } from '../services/sessionService'; //  SESSION: Import sessionService

import {
  SpecialRules,
  DEFAULT_SPECIAL_RULES,
  SPECIAL_RULES_INFO,
  generatePrivateCode,
} from '../types/GameState';
import '../styles/CreateTablePage.css';
import { isIOSVoiceVideoBlocked } from '../utils/iosUtils';

// ============================================================================
//  Tutorial: data-tutorial attributes added for spotlight highlighting

// This page is part of the new user walkthrough - Create Table section
// ============================================================================

// ============================================================================
// INFO TOOLTIP COMPONENT
// ============================================================================

interface InfoTooltipProps {
  label: string;
  children?: React.ReactNode;
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({ label, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <Tooltip
      label={label}
      isOpen={isOpen}
      placement="top"
      hasArrow
      bg="gray.700"
      color="white"
      p={3}
      borderRadius="md"
      maxW="300px"
      fontSize="sm"
    >
      <span
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'help' }}
      >
        {children || (
          <Icon as={InfoIcon} color="cyan.400" ml={2} w={4} h={4} />
        )}
      </span>
    </Tooltip>
  );
};

// ============================================================================
// SECTION HEADER COMPONENT
// ============================================================================

interface SectionHeaderProps {
  title: string;
  icon: string;
  tooltip: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, icon, tooltip }) => (
  <HStack mb={4} align="center">
    <Text fontSize="xl">{icon}</Text>
    <Heading size="md" color="white">{title}</Heading>
    <InfoTooltip label={tooltip} />
  </HStack>
);

// ============================================================================
// SPECIAL RULE TOGGLE COMPONENT
// ============================================================================

interface SpecialRuleToggleProps {
  ruleId: keyof SpecialRules;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  configValue?: number;
  onConfigChange?: (value: number) => void;
  configLabel?: string;
  configMin?: number;
  configMax?: number;
  configStep?: number;
  configSuffix?: string;
  disabled?: boolean;
  comingSoon?: boolean;
}

const SpecialRuleToggle: React.FC<SpecialRuleToggleProps> = ({
  ruleId,
  enabled,
  onToggle,
  configValue,
  onConfigChange,
  configLabel,
  configMin = 1,
  configMax = 10,
  configStep = 1,
  configSuffix = 'x BB',
  disabled = false,
  comingSoon = false,
}) => {
  const ruleInfo = SPECIAL_RULES_INFO.find(r => r.id === ruleId);
  if (!ruleInfo) return null;

  return (
    <Box
      className={`special-rule-card ${enabled ? 'enabled' : ''} ${disabled ? 'disabled' : ''}`}
      p={4}
      borderRadius="lg"
      bg={disabled ? 'rgba(100, 100, 100, 0.1)' : enabled ? 'rgba(0, 217, 217, 0.1)' : 'rgba(255, 255, 255, 0.05)'}
      border="1px solid"
      borderColor={disabled ? 'gray.700' : enabled ? 'cyan.500' : 'gray.600'}
      transition="all 0.2s"
      opacity={disabled ? 0.6 : 1}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      position="relative"
    >
      {/* Coming Soon Badge */}
      {comingSoon && (
        <Badge
          position="absolute"
          top={2}
          right={2}
          colorScheme="purple"
          fontSize="xs"
          px={2}
          py={0.5}
          borderRadius="full"
        >
          Coming Soon
        </Badge>
      )}
      
      <HStack justify="space-between" align="start">
        <HStack align="start" flex={1}>
          <Text fontSize="2xl" opacity={disabled ? 0.5 : 1}>{ruleInfo.icon}</Text>
          <VStack align="start" spacing={1} flex={1}>
            <HStack>
              <Text fontWeight="bold" color={disabled ? 'gray.500' : 'white'}>{ruleInfo.name}</Text>
              <InfoTooltip label={comingSoon ? `${ruleInfo.detailedExplanation}\n\n⏳ This feature is coming soon!` : ruleInfo.detailedExplanation} />
            </HStack>
            <Text fontSize="sm" color={disabled ? 'gray.600' : 'gray.400'}>{ruleInfo.description}</Text>
          </VStack>
        </HStack>
        <Switch
          isChecked={enabled}
          onChange={(e) => !disabled && onToggle(e.target.checked)}
          colorScheme="cyan"
          size="lg"
          isDisabled={disabled}
        />
      </HStack>
      
      {/* Config slider when enabled */}
      {enabled && configValue !== undefined && onConfigChange && !disabled && (
        <Box mt={4} pl={10}>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" color="gray.300">{configLabel}</Text>
            <Badge colorScheme="cyan" fontSize="sm">
              {configValue}{configSuffix}
            </Badge>
          </HStack>
          <Slider
            value={configValue}
            onChange={onConfigChange}
            min={configMin}
            max={configMax}
            step={configStep}
          >
            <SliderTrack bg="gray.600">
              <SliderFilledTrack bg="cyan.500" />
            </SliderTrack>
            <SliderThumb boxSize={5} bg="cyan.400" />
          </Slider>
          <HStack justify="space-between" mt={1}>
            <Text fontSize="xs" color="gray.500">{configMin}{configSuffix}</Text>
            <Text fontSize="xs" color="gray.500">{configMax}{configSuffix}</Text>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CreateTablePage: React.FC = () => {
  const navigate = useNavigate();
  // 🏠 HOME GAME mode (?mode=home): private, host-banked session chips,
  // rake-free, free to join - the app never touches money for these games.
  const [searchParams] = useSearchParams();
  const isHomeGame = searchParams.get('mode') === 'home';
  const toast = useToast();
  const currentUser = auth.currentUser;

  // ===== FORM STATE =====
  
  // Basic settings
  const [currency, setCurrency] = useState<'playcoins' | 'pkcash'>('playcoins');
  const [customName, setCustomName] = useState('');
  const [smallBlind, setSmallBlind] = useState(1);
  const [bigBlind, setBigBlind] = useState(2);
  const [minBuyIn, setMinBuyIn] = useState(40);
  const [maxBuyIn, setMaxBuyIn] = useState(200);
  const [maxSeats, setMaxSeats] = useState(8);
  
  // Privacy
  const [isPrivate, setIsPrivate] = useState(isHomeGame);
  const [privateCode, setPrivateCode] = useState('');
  const [useCustomCode, setUseCustomCode] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  
  // Special rules
  const [specialRules, setSpecialRules] = useState<SpecialRules>(DEFAULT_SPECIAL_RULES);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // UI state
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // HOME GAME: chips are host-banked scrip, so the table is always PlayCoins
  // and always private. The server forces both too - this keeps the form from
  // ever showing PSS Coins or a public option.
  useEffect(() => {
    if (isHomeGame) {
      setCurrency('playcoins');
      setIsPrivate(true);
    }
  }, [isHomeGame]);

  //  Voice Chat state

  // Voice is free to enable now (every player gets weekly free minutes);
  // the per-table fee became an optional unlimited upgrade below.
  const [voiceChatEnabled, setVoiceChatEnabled] = useState(true);
  const [unlimitedVoiceForTable, setUnlimitedVoiceForTable] = useState(false);
  // Table recording add-on: paid per session for regular players, free for partners
  // and for tables opened with a partner code.
  const [recordSession, setRecordSession] = useState(false);
  const [recordShowCards, setRecordShowCards] = useState(true);
  const [recordingRules, setRecordingRules] = useState<any>(null);
  useEffect(() => { gameApiService.recordingsRules().then(setRecordingRules).catch(() => {}); }, []);
  //  iOS: Voice/video not supported yet

  const iosVoiceBlocked = isIOSVoiceVideoBlocked();
  //  TABLE THEME: Premium table styling

  const [tableTheme, setTableTheme] = useState<'classic' | 'premium'>('premium');
  const [dealerImageUrl, setDealerImageUrl] = useState('/images/dealers/dealer-female-1.png');

  const [voicePromoCode, setVoicePromoCode] = useState('');
  const [voicePromoValidating, setVoicePromoValidating] = useState(false);
  //  FIX 1: Added codeId and agentId to state type

  const [voicePromoResult, setVoicePromoResult] = useState<{
    valid: boolean;
    free: boolean;
    agentName?: string;
    codeId?: string;      //  Store bypass code ID for payment processing

    agentId?: string;     //  Store agent/owner ID for tracking

    error?: string;
  } | null>(null);
  const [userPkCashBalance, setUserPkCashBalance] = useState(0);

  //  NEW: Geo restriction state

  const [isRestrictedState, setIsRestrictedState] = useState(false);
  const [voiceChatFee, setVoiceChatFee] = useState(voiceChatService.VOICE_CHAT_FEE);
  const [hasActiveVoiceSession, setHasActiveVoiceSession] = useState(false);
  const [currentStateCode, setCurrentStateCode] = useState<string | undefined>(undefined); //  NEW: Store state code


  //  SESSION: State for session tracking (private tables only)

  const [enableSession, setEnableSession] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [existingSessions, setExistingSessions] = useState<GameSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [loadingExistingSessions, setLoadingExistingSessions] = useState(false);

  //  NEW: Check geo location and voice pricing on mount

  useEffect(() => {
    const checkGeoAndPricing = async () => {
      if (!currentUser?.uid) return;
      
      try {
        // Check geo location (now includes admin bypass check automatically)
        const geoResult = await geoService.checkLocation();
        
        //  Use canPlayPKCash which respects admin bypass

        const restricted = geoResult.canPlayPKCash === false;
        
        setIsRestrictedState(restricted);
        setCurrentStateCode(geoResult.region); //  Store state code

        
        //  Force PlayCoins in restricted states (unless admin bypassed)

        if (restricted && !isHomeGame) {
          setCurrency('playcoins');
          //  Set appropriate default blinds for PlayCoins

          setSmallBlind(50);
          setBigBlind(100);
          setMinBuyIn(2000);
          setMaxBuyIn(10000);
        }
        
        if (geoResult.adminBypassed) {
          console.log('✅ [GEO] Admin bypass active - PSS Coins enabled');
        }
        
        // Get voice chat pricing for this location
        const pricing = await voiceChatService.getVoiceChatFee(currentUser.uid);
        setVoiceChatFee(pricing.fee);
        
        // Check for active voice session
        const activeSession = await voiceChatService.getActiveVoiceSession(currentUser.uid);
        setHasActiveVoiceSession(!!activeSession);
      } catch (error) {
        console.error('Error checking geo/pricing:', error);
      }
    };
    checkGeoAndPricing();
  }, [currentUser?.uid]);

  // Load user's PSS Coins balance for voice chat fee
  useEffect(() => {
    const loadBalance = async () => {
      if (currentUser?.uid) {
        const wallet = await walletService.getWallet(currentUser.uid);
        setUserPkCashBalance(wallet?.pkCash || 0);
      }
    };
    loadBalance();
  }, [currentUser?.uid]);

  // ===== AUTO-CALCULATIONS =====
  
  //  Update default blinds when currency changes

  useEffect(() => {
    // Home game chips are scrip - 1/2 chip blinds are normal, don't bump them.
    if (isHomeGame) return;
    if (currency === 'playcoins') {
      // PlayCoins: Higher stakes since it's play money
      if (smallBlind < 10) {
        setSmallBlind(50);
        setBigBlind(100);
      }
    } else {
      // PSS Coins: Lower stakes for real money
      if (smallBlind > 20) {
        setSmallBlind(1);
        setBigBlind(2);
      }
    }
  }, [currency, isHomeGame]);
  
  // Auto-calculate buy-in when blinds change
  useEffect(() => {
    const recommendedMin = bigBlind * 20;
    const recommendedMax = bigBlind * 100;
    setMinBuyIn(recommendedMin);
    setMaxBuyIn(recommendedMax);
  }, [bigBlind]);

  // Auto-generate private code when private is enabled
  useEffect(() => {
    if (isPrivate && !useCustomCode && !privateCode) {
      setPrivateCode(generatePrivateCode());
    }
  }, [isPrivate, useCustomCode]);

  //  SESSION: Load existing sessions when private table is selected

  useEffect(() => {
    const loadExistingSessions = async () => {
      if (!isPrivate || !currentUser?.uid) {
        setExistingSessions([]);
        return;
      }
      
      setLoadingExistingSessions(true);
      try {
        const sessions = await sessionService.getRecentSessions();
        setExistingSessions(sessions);
        
        // Auto-suggest session name based on day
        if (!sessionName) {
          const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const day = days[new Date().getDay()];
          setSessionName(`${day} Night Poker`);
        }
      } catch (error) {
        console.error('Error loading existing sessions:', error);
      } finally {
        setLoadingExistingSessions(false);
      }
    };
    
    loadExistingSessions();
  }, [isPrivate, currentUser?.uid]);

  // ===== COMPUTED VALUES =====
  
  const hasSpecialRules = useMemo(() => {
    return (
      specialRules.sevenTwoGame ||
      specialRules.bombPotEnabled ||
      specialRules.straddleEnabled ||
      specialRules.runItTwiceEnabled ||
      specialRules.allInOrFold
    );
  }, [specialRules]);

  const tablePreview = useMemo(() => {
    const parts: string[] = [];
    parts.push(`${smallBlind}/${bigBlind}`);
    parts.push(isHomeGame ? 'Home Chips' : currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins');
    parts.push(`${maxSeats} seats`);
    if (isPrivate) parts.push('Private');
    if (hasSpecialRules) parts.push('Special Rules');
    if (tableTheme === 'premium') parts.push(' Premium');

    return parts.join(' • ');
  }, [smallBlind, bigBlind, currency, maxSeats, isPrivate, hasSpecialRules]);

  // ===== VALIDATION =====
  
  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    
    // Blinds validation
    if (smallBlind <= 0) newErrors.smallBlind = 'Must be greater than 0';
    if (bigBlind <= 0) newErrors.bigBlind = 'Must be greater than 0';
    if (bigBlind < smallBlind) newErrors.bigBlind = 'Must be >= small blind';
    
    // Buy-in validation
    if (minBuyIn <= 0) newErrors.minBuyIn = 'Must be greater than 0';
    if (maxBuyIn <= 0) newErrors.maxBuyIn = 'Must be greater than 0';
    if (minBuyIn > maxBuyIn) newErrors.minBuyIn = 'Cannot exceed max buy-in';
    
    // Seats validation
    if (maxSeats < 2 || maxSeats > 10) newErrors.maxSeats = 'Must be 2-10';
    
    // Private code validation
    if (isPrivate && useCustomCode && !privateCode.trim()) {
      newErrors.privateCode = 'Code is required for private tables';
    }
    
    // Name validation
    if (customName.length > 50) newErrors.customName = 'Max 50 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ===== HANDLERS =====
  
  const handleSpecialRuleToggle = (ruleId: keyof SpecialRules, enabled: boolean) => {
    setSpecialRules(prev => ({ ...prev, [ruleId]: enabled }));
    // All-in or Fold plays a fixed short stack: everyone sits with 8 big
    // blinds and the server tops up / banks after every hand. Lock the
    // buy-in range to that stack so the table matches the house format.
    if (ruleId === 'allInOrFold' && enabled) {
      const stack = bigBlind * (specialRules.aofStackBB || 8);
      setMinBuyIn(stack);
      setMaxBuyIn(stack);
    }
  };

  const handleSpecialRuleConfig = (ruleId: keyof SpecialRules, value: number) => {
    setSpecialRules(prev => ({ ...prev, [ruleId]: value }));
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(privateCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
    toast({
      title: 'Code copied!',
      description: 'Share this code with friends to let them join.',
      status: 'success',
      duration: 2000,
    });
  };

  const handleRegenerateCode = () => {
    setPrivateCode(generatePrivateCode());
  };

  //  Voice Chat Promo Code Validation

  const handleValidatePromoCode = async () => {
    if (!voicePromoCode.trim()) return;
    
    setVoicePromoValidating(true);
    setVoicePromoResult(null);
    
    try {
      const result = await voiceChatService.validatePromoCode(voicePromoCode);
      
      //  FIX 2: Store codeId and agentId from validation result

      if (result.isValid && result.isFreePromo) {
        setVoicePromoResult({
          valid: true,
          free: true,
          agentName: result.agentName,
          codeId: result.codeId,       //  Store code ID for payment processing

          agentId: result.agentId,     //  Store agent ID for tracking

        });
        toast({
          title: 'Promo Code Applied!',

          description: `Voice chat fee waived by ${result.agentName}`,
          status: 'success',
          duration: 3000,
        });
      } else {
        setVoicePromoResult({
          valid: false,
          free: false,
          error: result.error || 'Invalid promo code',
        });
      }
    } catch (error) {
      setVoicePromoResult({
        valid: false,
        free: false,
        error: 'Failed to validate code',
      });
    } finally {
      setVoicePromoValidating(false);
    }
  };

  const handleCreate = async () => {
    if (!currentUser) {
      toast({
        title: 'Not logged in',
        description: 'Please log in to create a table.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before creating.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setCreating(true);

    try {

      const result = await enhancedTableService.createEnhancedTable({
        ownerId: currentUser.uid,
        currency: isHomeGame ? 'playcoins' : currency,
        smallBlind,
        bigBlind,
        customName: customName.trim() || undefined,
        maxSeats,
        // All-in or Fold: fixed stack regardless of what the inputs held
        // before the toggle (the server enforces the same).
        minBuyIn: specialRules.allInOrFold ? bigBlind * (specialRules.aofStackBB || 8) : minBuyIn,
        maxBuyIn: specialRules.allInOrFold ? bigBlind * (specialRules.aofStackBB || 8) : maxBuyIn,
        isPrivate: isHomeGame ? true : isPrivate,
        chipMode: isHomeGame ? ('session' as const) : undefined,
        privateCode: isPrivate ? privateCode : undefined,
        specialRules,
        voiceChatEnabled, //  Pass voice chat setting

        stateCode: currentStateCode, //  NEW: Pass state code for geo check

        //  PREMIUM: Theme settings

        tableTheme,
        dealerImageUrl: tableTheme === 'premium' ? dealerImageUrl : undefined,
      });

      if (result.success && result.tableId) {
        //  SESSION: Create or link session for private tables with session enabled

        if (isPrivate && enableSession) {
          try {
            if (selectedSessionId) {
              // Link to existing session
              await sessionService.addTableToSession(selectedSessionId, result.tableId);
              console.log(`✅ [SESSION] Linked table to existing session ${selectedSessionId}`);
            } else {
              // Create new session
              const sessionResult = await sessionService.createSession(
                sessionName || 'Poker Session',
                result.tableId,
                currency,
                customName.trim() || undefined
              );
              if (sessionResult.success) {
                console.log(`✅ [SESSION] Created new session ${sessionResult.sessionId}`);
              }
            }
          } catch (sessionError) {
            console.error('Session creation/linking failed:', sessionError);
            // Don't block table creation if session fails
          }
        }

        // Optional per-table unlimited voice (paid, or waived by a promo code)
        if (voiceChatEnabled && unlimitedVoiceForTable) {
          const promo = voicePromoResult?.valid && voicePromoResult?.free && voicePromoResult?.codeId
            ? { promoCode: voicePromoCode, codeId: voicePromoResult.codeId, agentId: voicePromoResult.agentId }
            : undefined;
          const unlock = await unlockTableVoice(result.tableId, promo);
          if (!unlock.success) {
            toast({ title: 'Table created, unlimited voice not applied', description: unlock.error, status: 'warning', duration: 6000 });
          }
        }

        toast({
          title: 'Table Created!',
          description: isPrivate 
            ? `Private code: ${privateCode}${enableSession ? ' • Session tracking enabled' : ''}` 
            : voiceChatEnabled 
              ? 'Your table is ready with voice chat!' 
              : 'Your table is ready.',
          status: 'success',
          duration: 3000,
        });
        // ?host=1 (from the "Host a home game" popup) opens the invite panel
        // as soon as the host is seated at the new table.
        const openParams = new URLSearchParams();
        if (voiceChatEnabled && recordSession) { openParams.set('record', '1'); openParams.set('cards', recordShowCards ? '1' : '0'); }
        if (searchParams.get('host') === '1') openParams.set('host', '1');
        const openQuery = openParams.toString();
        navigate(`/game/${result.tableId}${openQuery ? `?${openQuery}` : ''}`);
      } else {
        toast({
          title: 'Creation Failed',
          description: result.error || 'Unknown error occurred.',
          status: 'error',
          duration: 5000,
        });
      }
    } catch (error: any) {
      console.error('Error creating table:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create table.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setCreating(false);
    }
  };

  // ===== RENDER =====
  
  return (
    <div className="create-table-page">
      <div className="create-table-container">
        {/* Header */}
        <div className="page-header" data-tutorial="create-table-header">
          <button className="back-button" onClick={() => navigate('/tables')}>
            ← Back to Tables
          </button>
          <h1>{isHomeGame ? 'Host a Home Game' : 'Create New Table'}</h1>
          <p className="preview-text">{tablePreview}</p>
        </div>

        {/* HOME GAME MODE - explains the host-banked model up front */}
        {isHomeGame && (
          <div style={{
            margin: '0 0 20px',
            padding: '16px 18px',
            background: 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(59,130,246,0.10))',
            border: '2px solid rgba(139,92,246,0.5)',
            borderRadius: '14px',
            color: '#e2e8f0',
            fontSize: '14px',
            lineHeight: 1.6,
          }}>
            <strong style={{ color: '#a78bfa' }}>Home Game</strong>
            <div style={{ marginTop: '6px', color: '#94a3b8' }}>
              Private and rake-free. Friends join FREE with a starting stack, no
              wallet needed - perfect for invites to people new to the app. You
              assign or remove chips from the table as the host. Chips are
              session-only scorekeeping with no cash value and never touch
              anyone's wallet.
            </div>
          </div>
        )}

        {/* ============================================================================ */}
        {/* CURRENCY SELECTION - Tutorial Step 1 */}
        {/* ============================================================================ */}
        <div className="form-section" data-tutorial="currency-select">
          <SectionHeader
            title="Currency"
            icon=""

            tooltip={isHomeGame
              ? 'Home games always use PlayCoins. Chips are session-only scorekeeping the host assigns - no cash value, no wallets.'
              : 'PlayCoins are free to play. PSS Coins have real value and can be redeemed.'}
          />

          {/*  NEW: Restricted state warning */}

          {!isHomeGame && isRestrictedState && (
            <div style={{ 
              background: 'rgba(245, 101, 101, 0.1)', 
              border: '1px solid #F56565',
              borderRadius: '8px', 
              padding: '12px', 
              marginBottom: '12px',
              fontSize: '14px',
              color: '#FC8181'
            }}>
               PSS Coins are not available in your state. You can play with PlayCoins!

              <br />
              <span style={{ fontSize: '12px', color: '#A0AEC0' }}>
                Your PSS Coins balance is safe and will be available when you're in a legal state.
              </span>
            </div>
          )}
          
          {isHomeGame ? (
            // Home games are locked to PlayCoins - the picker would only mislead.
            <div className="currency-toggle">
              <button className="currency-btn active" disabled style={{ cursor: 'default' }}>
                 PlayCoins

                <span className="currency-desc">Home game chips - no cash value</span>
              </button>
            </div>
          ) : (
          <div className="currency-toggle">
            <button
              className={`currency-btn ${currency === 'playcoins' ? 'active' : ''}`}
              onClick={() => setCurrency('playcoins')}
            >
               PlayCoins

              <span className="currency-desc">Free to play</span>
            </button>
            <button
              className={`currency-btn ${currency === 'pkcash' ? 'active' : ''} ${isRestrictedState ? 'disabled' : ''}`}
              onClick={() => !isRestrictedState && setCurrency('pkcash')}
              disabled={isRestrictedState}
              style={isRestrictedState ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            >
               PSS Coins

              <span className="currency-desc">{isRestrictedState ? 'Not available' : 'Real value'}</span>
            </button>
          </div>
          )}
        </div>

        {/* ============================================================================ */}
        {/* TABLE NAME - Tutorial Step 2 */}
        {/* ============================================================================ */}
        <div className="form-section" data-tutorial="table-name">
          <SectionHeader 
            title="Table Name" 
            icon="" 

            tooltip="Give your table a memorable name. Leave blank for an auto-generated name."
          />
          <input
            type="text"
            className={`form-input ${errors.customName ? 'error' : ''}`}
            placeholder="Enter custom name or leave blank for random"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            maxLength={50}
          />
          {errors.customName && <span className="error-text">{errors.customName}</span>}
          <span className="hint-text">{50 - customName.length} characters remaining</span>
        </div>

        {/* ============================================================================ */}
        {/* BLINDS - Tutorial Step 3 */}
        {/* ============================================================================ */}
        <div className="form-section" data-tutorial="blinds-section">
          <SectionHeader 
            title="Blinds" 
            icon="" 

            tooltip="Small Blind is the forced bet from the player left of the dealer. Big Blind is typically 2x the Small Blind."
          />
          <div className="blinds-grid">
            <div className="input-group">
              <label>Small Blind</label>
              <input
                type="number"
                className={`form-input ${errors.smallBlind ? 'error' : ''}`}
                value={smallBlind}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  setSmallBlind(val);
                  if (bigBlind < val * 2) setBigBlind(val * 2);
                }}
                min={0.01}
                step={0.01}
              />
              {errors.smallBlind && <span className="error-text">{errors.smallBlind}</span>}
            </div>
            <div className="input-group">
              <label>Big Blind</label>
              <input
                type="number"
                className={`form-input ${errors.bigBlind ? 'error' : ''}`}
                value={bigBlind}
                onChange={(e) => setBigBlind(parseFloat(e.target.value) || 0)}
                min={smallBlind}
                step={0.01}
              />
              {errors.bigBlind && <span className="error-text">{errors.bigBlind}</span>}
            </div>
          </div>
          <div className="preset-buttons" data-tutorial="blinds-presets">
            {isHomeGame ? (
              <>
                <button onClick={() => { setSmallBlind(1); setBigBlind(2); }}>1/2</button>
                <button onClick={() => { setSmallBlind(2); setBigBlind(5); }}>2/5</button>
                <button onClick={() => { setSmallBlind(5); setBigBlind(10); }}>5/10</button>
                <button onClick={() => { setSmallBlind(10); setBigBlind(25); }}>10/25</button>
                <button onClick={() => { setSmallBlind(25); setBigBlind(50); }}>25/50</button>
                <button onClick={() => { setSmallBlind(50); setBigBlind(100); }}>50/100</button>
              </>
            ) : currency === 'pkcash' ? (
              <>
                <button onClick={() => { setSmallBlind(0.05); setBigBlind(0.10); }}>$0.05/$0.10</button>
                <button onClick={() => { setSmallBlind(0.25); setBigBlind(0.50); }}>$0.25/$0.50</button>
                <button onClick={() => { setSmallBlind(0.5); setBigBlind(1); }}>$0.50/$1</button>
                <button onClick={() => { setSmallBlind(1); setBigBlind(2); }}>$1/$2</button>
                <button onClick={() => { setSmallBlind(2); setBigBlind(5); }}>$2/$5</button>
                <button onClick={() => { setSmallBlind(5); setBigBlind(10); }}>$5/$10</button>
              </>
            ) : (
              <>
                <button onClick={() => { setSmallBlind(25); setBigBlind(50); }}>25/50</button>
                <button onClick={() => { setSmallBlind(50); setBigBlind(100); }}>50/100</button>
                <button onClick={() => { setSmallBlind(100); setBigBlind(200); }}>100/200</button>
                <button onClick={() => { setSmallBlind(250); setBigBlind(500); }}>250/500</button>
                <button onClick={() => { setSmallBlind(500); setBigBlind(1000); }}>500/1K</button>
                <button onClick={() => { setSmallBlind(1000); setBigBlind(2000); }}>1K/2K</button>
              </>
            )}
          </div>
        </div>

        {/* ============================================================================ */}
        {/* BUY-IN RANGE - Tutorial Step 4 */}
        {/* ============================================================================ */}
        <div className="form-section" data-tutorial="buyin-range">
          <SectionHeader 
            title="Buy-In Range" 
            icon="" 

            tooltip="Min/Max chips players can bring to the table. Standard is 20-100x Big Blind. Short stack games use 20x, deep stack uses 200x."
          />
          <div className="blinds-grid">
            <div className="input-group">
              <label>Minimum</label>
              <input
                type="number"
                className={`form-input ${errors.minBuyIn ? 'error' : ''}`}
                value={specialRules.allInOrFold ? bigBlind * (specialRules.aofStackBB || 8) : minBuyIn}
                onChange={(e) => setMinBuyIn(parseFloat(e.target.value) || 0)}
                min={bigBlind}
                disabled={specialRules.allInOrFold}
              />
              {errors.minBuyIn && <span className="error-text">{errors.minBuyIn}</span>}
            </div>
            <div className="input-group">
              <label>Maximum</label>
              <input
                type="number"
                className={`form-input ${errors.maxBuyIn ? 'error' : ''}`}
                value={specialRules.allInOrFold ? bigBlind * (specialRules.aofStackBB || 8) : maxBuyIn}
                onChange={(e) => setMaxBuyIn(parseFloat(e.target.value) || 0)}
                min={minBuyIn}
                disabled={specialRules.allInOrFold}
              />
              {errors.maxBuyIn && <span className="error-text">{errors.maxBuyIn}</span>}
            </div>
          </div>
          <span className="hint-text">
            {specialRules.allInOrFold
              ? `All-in or Fold: everyone sits with ${(specialRules.aofStackBB || 8)} big blinds (${bigBlind * (specialRules.aofStackBB || 8)}), topped up from the wallet after every hand.`
              : `Suggested: Min = ${bigBlind * 20} (20x BB), Max = ${bigBlind * 100} (100x BB)`}
          </span>
        </div>

        {/* ============================================================================ */}
        {/* TABLE SIZE - Tutorial Step 5 */}
        {/* ============================================================================ */}
        <div className="form-section" data-tutorial="seats-section">
          <SectionHeader 
            title="Table Size" 
            icon="" 

            tooltip="2 players = Heads-Up (1v1). 6 players = Short-handed (faster action). 9-10 = Full Ring (more players, more patience needed)."
          />
          <div className="seats-slider">
            <input
              type="range"
              min={2}
              max={10}
              value={maxSeats}
              onChange={(e) => setMaxSeats(parseInt(e.target.value))}
              className="slider"
            />
            <div className="seats-display">
              <span className="seats-number">{maxSeats}</span>
              <span className="seats-label">
                {maxSeats === 2 ? 'Heads-Up' : maxSeats <= 6 ? 'Short-handed' : 'Full Ring'}
              </span>
            </div>
          </div>
          <div className="seats-markers">
            <span>2</span>
            <span>4</span>
            <span>6</span>
            <span>8</span>
            <span>10</span>
          </div>
        </div>

        {/* ============================================================================ */}
        {/* PRIVACY - Tutorial Step 6 */}
        {/* ============================================================================ */}
        <div className="form-section" data-tutorial="privacy-section">
          <SectionHeader 
            title="Privacy" 
            icon={isPrivate ? '' : ''} 

            tooltip={isHomeGame
              ? 'Home games are always private. Share the invite code with your group - the table never appears in the public lobby.'
              : 'Public tables appear in the lobby for anyone to join. Private tables require an access code.'}
          />
          {isHomeGame ? (
            // Home games are locked to private - no public option exists.
            <div className="privacy-toggle">
              <button className="privacy-btn active" disabled style={{ cursor: 'default' }}>
                 Private

                <span className="privacy-desc">Home games are always private</span>
              </button>
            </div>
          ) : (
          <div className="privacy-toggle">
            <button
              className={`privacy-btn ${!isPrivate ? 'active' : ''}`}
              onClick={() => setIsPrivate(false)}
            >
               Public

              <span className="privacy-desc">Anyone can join</span>
            </button>
            <button
              className={`privacy-btn ${isPrivate ? 'active' : ''}`}
              onClick={() => setIsPrivate(true)}
            >
               Private

              <span className="privacy-desc">Code required</span>
            </button>
          </div>
          )}
          
          {isPrivate && (
            <div className="private-code-section" data-tutorial="private-code-config">
              <div className="code-type-toggle">
                <label>
                  <input
                    type="checkbox"
                    checked={useCustomCode}
                    onChange={(e) => {
                      setUseCustomCode(e.target.checked);
                      if (!e.target.checked) {
                        setPrivateCode(generatePrivateCode());
                      }
                    }}
                  />
                  Use custom code
                </label>
              </div>
              
              {useCustomCode ? (
                <input
                  type="text"
                  className={`form-input ${errors.privateCode ? 'error' : ''}`}
                  placeholder="Enter your custom code"
                  value={privateCode}
                  onChange={(e) => setPrivateCode(e.target.value)}
                  maxLength={20}
                />
              ) : (
                <div className="generated-code">
                  <span className="code-value">{privateCode}</span>
                  <button className="code-btn" onClick={handleCopyCode}>
                    {codeCopied ? ' Copied' : ' Copy'}

                  </button>
                  <button className="code-btn" onClick={handleRegenerateCode}>
                     New Code

                  </button>
                </div>
              )}
              {errors.privateCode && <span className="error-text">{errors.privateCode}</span>}
              <span className="hint-text">Share this code with friends to let them join your private table.</span>
            </div>
          )}
          
          {/*  SESSION: Session Tracking for Private Tables. Hidden for home
              games - those get an automatic buy-in/cash-out ledger already. */}

          {isPrivate && !isHomeGame && (
            <div className="session-tracking-section" style={{ marginTop: '16px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '12px',
                background: 'rgba(139, 92, 246, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)'
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span></span>

                    <span style={{ fontWeight: 'bold', color: '#fff' }}>Session Tracking</span>
                    <InfoTooltip label="Track buy-ins and cash-outs across multiple tables. Great for home games that may restart due to crashes. Export results at the end for bragging rights!" />
                  </div>
                  <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                    Track ledger across table restarts
                  </span>
                </div>
                <Switch
                  isChecked={enableSession}
                  onChange={(e) => {
                    setEnableSession(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedSessionId(null);
                    }
                  }}
                  colorScheme="purple"
                  size="lg"
                />
              </div>
              
              {enableSession && (
                <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                  {/* Session Mode Selection */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '8px' }}>
                      <input
                        type="radio"
                        name="sessionMode"
                        checked={!selectedSessionId}
                        onChange={() => setSelectedSessionId(null)}
                        style={{ accentColor: '#8b5cf6' }}
                      />
                      <span style={{ color: '#fff' }}>Start New Session</span>
                    </label>
                    
                    {existingSessions.length > 0 && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="sessionMode"
                          checked={!!selectedSessionId}
                          onChange={() => setSelectedSessionId(existingSessions[0]?.id || null)}
                          style={{ accentColor: '#8b5cf6' }}
                        />
                        <span style={{ color: '#fff' }}>Continue Existing Session</span>
                      </label>
                    )}
                  </div>
                  
                  {/* New Session Name */}
                  {!selectedSessionId && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px', display: 'block' }}>
                        Session Name
                      </label>
                      <input
                        type="text"
                        value={sessionName}
                        onChange={(e) => setSessionName(e.target.value)}
                        placeholder="Saturday Night Poker"
                        maxLength={50}
                        className="form-input"
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                  
                  {/* Existing Sessions List */}
                  {selectedSessionId && existingSessions.length > 0 && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px', display: 'block' }}>
                        Select Session
                      </label>
                      {loadingExistingSessions ? (
                        <div style={{ textAlign: 'center', padding: '12px' }}>
                          <Spinner size="sm" color="purple.400" />
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {existingSessions.map((session) => (
                            <div
                              key={session.id}
                              onClick={() => setSelectedSessionId(session.id)}
                              style={{
                                padding: '10px 12px',
                                background: selectedSessionId === session.id 
                                  ? 'rgba(139, 92, 246, 0.2)' 
                                  : 'rgba(255,255,255,0.05)',
                                border: selectedSessionId === session.id 
                                  ? '1px solid #8b5cf6' 
                                  : '1px solid transparent',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              <div style={{ fontWeight: 'bold', color: '#fff' }}>{session.name}</div>
                              <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                                {Object.keys(session.players || {}).length} players • {session.tableIds?.length || 1} table(s)
                                <span style={{ marginLeft: '8px', color: '#8b5cf6' }}>
                                  {session.code}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div style={{ marginTop: '12px', fontSize: '12px', color: '#6B7280' }}>
                     Session data exports to JSON for record keeping. Automatically tracks across table restarts.

                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ============================================================================ */}
        {/* VOICE & VIDEO CHAT */}
        {/* ============================================================================ */}
        <div className="form-section" data-tutorial="voice-video-section">
          <SectionHeader 
            title="Voice & Video Chat" 
            icon="" 

            tooltip="Enable real-time voice and video communication at your table. Players can talk and see each other during the game."
          />
          
          <Box
            p={5}
            borderRadius="xl"
            bg={voiceChatEnabled ? 'rgba(0, 217, 217, 0.08)' : 'rgba(255, 255, 255, 0.03)'}
            border="1px solid"
            borderColor={voiceChatEnabled ? 'cyan.600' : 'gray.700'}
            transition="all 0.3s"
            opacity={iosVoiceBlocked ? 0.6 : 1}
          >
            {/*  iOS Block Banner */}

            {iosVoiceBlocked && (
              <Box
                mb={4}
                p={3}
                borderRadius="md"
                bg="rgba(245, 158, 11, 0.1)"
                border="1px solid"
                borderColor="yellow.600"
              >
                <HStack spacing={2}>
                  <Text fontSize="sm"></Text>

                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" color="yellow.300" fontWeight="bold">
                      Voice & Video available on Web browsers
                    </Text>
                    <Text fontSize="xs" color="yellow.200">
                      Open in Safari or Chrome for full Voice & Video access.
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            )}

            {/* Header with toggle */}
            <HStack justify="space-between" align="center" mb={voiceChatEnabled ? 4 : 0}>
              <HStack spacing={3}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bg={voiceChatEnabled ? 'rgba(0, 217, 217, 0.15)' : 'rgba(255, 255, 255, 0.05)'}
                >
                  <Text fontSize="xl"></Text>

                </Box>
                <VStack align="start" spacing={0}>
                  <Text fontWeight="bold" color="white" fontSize="md">Enable Voice & Video</Text>
                  <Text fontSize="xs" color="gray.500">
                    {iosVoiceBlocked ? 'Not available on iOS yet' : 'Real-time communication'}
                  </Text>
                </VStack>
              </HStack>
              <Switch
                isChecked={voiceChatEnabled}
                onChange={(e) => {
                  if (iosVoiceBlocked) return; //  Block on iOS

                  setVoiceChatEnabled(e.target.checked);
                  if (!e.target.checked) {
                    setVoicePromoCode('');
                    setVoicePromoResult(null);
                  }
                }}
                colorScheme="cyan"
                size="lg"
                isDisabled={iosVoiceBlocked}
              />
            </HStack>
            
            {voiceChatEnabled && (
              <VStack align="stretch" spacing={3}>
                <Text fontSize="sm" color="gray.400" lineHeight="1.6">
                  Players at this table can talk and turn on their camera. Everyone gets 45 free minutes of voice and video a week; after that a 24-hour pass or a per-table unlock keeps it going.
                </Text>
                <HStack justify="space-between" align="center" p={3} bg="rgba(0,217,217,0.06)" border="1px solid" borderColor="rgba(0,217,217,0.25)" borderRadius="lg">
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="bold" color="white" fontSize="sm">Unlimited voice for this table</Text>
                    <Text fontSize="xs" color="gray.500">$2.99 PSS Coins, one time. Nobody at this table uses their free minutes.</Text>
                  </VStack>
                  <Switch
                    isChecked={unlimitedVoiceForTable}
                    onChange={(e) => {
                      setUnlimitedVoiceForTable(e.target.checked);
                      if (!e.target.checked) { setVoicePromoCode(''); setVoicePromoResult(null); }
                    }}
                    colorScheme="cyan"
                  />
                </HStack>
                {(() => {
                  const r = recordingRules;
                  const partnerCode = !!(voicePromoResult?.valid && voicePromoResult?.free);
                  const free = !!r?.free || partnerCode;
                  const price = Number(r?.pricePssCoins ?? 2);
                  const canAfford = free || Number(r?.balance || 0) >= price;
                  const unavailable = !r || r.enabled === false;
                  return (
                    <HStack justify="space-between" align="center" p={3} bg="rgba(239,68,68,0.06)" border="1px solid" borderColor="rgba(239,68,68,0.35)" borderRadius="lg">
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="bold" color="white" fontSize="sm">Record this session</Text>
                        <Text fontSize="xs" color="gray.500">
                          {free
                            ? (r?.partner ? 'Free with your partner account.' : partnerCode ? 'Free with the partner code above.' : 'Free.')
                            : `${price} PSS Coins, charged when the table opens. Up to ${r?.maxSessionMinutes ?? 30} minutes.`}
                          {' '}Everyone at the table sees a REC badge and is asked to agree. Voice and video without recording stay free.
                        </Text>
                        {!r && <Text fontSize="xs" color="gray.400">Checking availability...</Text>}
                        {r && r.enabled === false && <Text fontSize="xs" color="orange.300">Recording isn't switched on yet.</Text>}
                        {!unavailable && !free && !canAfford && <Text fontSize="xs" color="orange.300">Not enough PSS Coins for a recording.</Text>}
                      </VStack>
                      <Switch
                        isChecked={recordSession}
                        onChange={(e) => setRecordSession(e.target.checked)}
                        colorScheme="red"
                        isDisabled={unavailable || (!free && !canAfford)}
                      />
                    </HStack>
                  );
                })()}
                {recordSession && (
                  <HStack justify="space-between" align="center" px={3}>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="bold" color="white" fontSize="sm">Show my cards in the recording</Text>
                      <Text fontSize="xs" color="gray.500">Only your own cards. Other players' cards stay hidden until showdown.</Text>
                    </VStack>
                    <Switch isChecked={recordShowCards} onChange={(e) => setRecordShowCards(e.target.checked)} colorScheme="cyan" />
                  </HStack>
                )}
                {unlimitedVoiceForTable && (
                  <HStack spacing={2}>
                    <Input
                      placeholder="Promo code (optional)"
                      value={voicePromoCode}
                      onChange={(e) => { setVoicePromoCode(e.target.value.toUpperCase()); setVoicePromoResult(null); }}
                      bg="rgba(0,0,0,0.3)"
                      borderColor={voicePromoResult?.valid ? 'green.400' : 'whiteAlpha.300'}
                      color="white"
                      size="sm"
                      maxW="220px"
                    />
                    <Button size="sm" variant="outline" colorScheme="cyan" onClick={handleValidatePromoCode} isLoading={voicePromoValidating} isDisabled={!voicePromoCode.trim()}>
                      Apply
                    </Button>
                    {voicePromoResult?.valid && voicePromoResult?.free && (
                      <Text fontSize="xs" color="green.300">Fee waived</Text>
                    )}
                    {voicePromoResult && !voicePromoResult.valid && (
                      <Text fontSize="xs" color="red.300">Invalid code</Text>
                    )}
                  </HStack>
                )}
              </VStack>
            )}
          </Box>
        </div>

        {/* ============================================================================ */}
        {/* SPECIAL RULES - Tutorial Step 7 */}
        {/* ============================================================================ */}
        <div className="form-section special-rules-section" data-tutorial="special-rules">
          <div 
            className="section-toggle"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <SectionHeader 
              title="Special Rules" 
              icon="" 

              tooltip="Optional game modes that add extra excitement. These are set when creating the table and cannot be changed during the game."
            />
            <span className={`toggle-arrow ${showAdvanced ? 'open' : ''}`}>
              {showAdvanced ? '▲' : '▼'}
            </span>
          </div>
          
          {hasSpecialRules && !showAdvanced && (
            <div className="active-rules-preview">
              {specialRules.sevenTwoGame && <span className="rule-badge"> 7-2</span>}

              {specialRules.bombPotEnabled && <span className="rule-badge"> Bomb</span>}

              {specialRules.straddleEnabled && <span className="rule-badge">⬆ Straddle</span>}

              {specialRules.runItTwiceEnabled && <span className="rule-badge"> Run 2x</span>}

              {specialRules.allInOrFold && <span className="rule-badge"> AoF</span>}

            </div>
          )}
          
          {showAdvanced && (
            <div className="special-rules-grid">
              {/* 7-2 Bounty */}
              <SpecialRuleToggle
                ruleId="sevenTwoGame"
                enabled={specialRules.sevenTwoGame}
                onToggle={(enabled) => handleSpecialRuleToggle('sevenTwoGame', enabled)}
                configValue={specialRules.sevenTwoBounty}
                onConfigChange={(value) => handleSpecialRuleConfig('sevenTwoBounty', value)}
                configLabel="Bounty Amount (per player)"
                configMin={1}
                configMax={10}
                configSuffix="x BB"
                disabled={false}
                comingSoon={false}
              />
              
              {/* Bomb Pot */}
              <SpecialRuleToggle
                ruleId="bombPotEnabled"
                enabled={specialRules.bombPotEnabled}
                onToggle={(enabled) => handleSpecialRuleToggle('bombPotEnabled', enabled)}
                configValue={specialRules.bombPotAnte}
                onConfigChange={(value) => handleSpecialRuleConfig('bombPotAnte', value)}
                configLabel="Default Ante (per player)"
                configMin={1}
                configMax={10}
                configSuffix="x BB"
                disabled={false}
                comingSoon={false}
              />
              
              {/* Straddle */}
              <SpecialRuleToggle
                ruleId="straddleEnabled"
                enabled={specialRules.straddleEnabled}
                onToggle={(enabled) => handleSpecialRuleToggle('straddleEnabled', enabled)}
                configValue={specialRules.straddleMax}
                onConfigChange={(value) => {
                  setSpecialRules(prev => ({
                    ...prev,
                    straddleMax: value,
                    straddleMin: Math.min(prev.straddleMin, value),
                  }));
                }}
                configLabel="Maximum Straddle"
                configMin={2}
                configMax={8}
                configSuffix="x BB"
                disabled={false}
                comingSoon={false}
              />
              
              {/* Run It Twice */}
              <SpecialRuleToggle
                ruleId="runItTwiceEnabled"
                enabled={specialRules.runItTwiceEnabled}
                onToggle={(enabled) => handleSpecialRuleToggle('runItTwiceEnabled', enabled)}
                disabled={false}
                comingSoon={false}
              />
              
              {/* All-In or Fold */}
              <SpecialRuleToggle
                ruleId="allInOrFold"
                enabled={specialRules.allInOrFold}
                onToggle={(enabled) => handleSpecialRuleToggle('allInOrFold', enabled)}
                disabled={false}
                comingSoon={false}
              />
            </div>
          )}
        </div>

        {/* ============================================================================ */}
        {/* TABLE THEME SELECTION */}
        {/* ============================================================================ */}
        <div className="form-section" data-tutorial="table-theme">
          <h3 style={{ 
            color: '#fff', 
            fontSize: '16px', 
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
             Table Theme

          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
          }}>
            <button
              type="button"
              onClick={() => setTableTheme('classic')}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px 20px',
                background: tableTheme === 'classic' 
                  ? 'rgba(0, 217, 217, 0.15)' 
                  : 'rgba(255, 255, 255, 0.05)',
                border: tableTheme === 'classic' 
                  ? '2px solid #00D9D9' 
                  : '2px solid #333',
                borderRadius: '16px',
                color: tableTheme === 'classic' ? '#00D9D9' : '#888',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            >
              <span style={{ marginBottom: '10px', display: 'flex' }}><Spade size={36} /></span>
              <span style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>Classic</span>
              <span style={{ fontSize: '12px', opacity: 0.7 }}>Standard table design</span>
            </button>
            
            <button
              type="button"
              onClick={() => setTableTheme('premium')}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px 20px',
                background: tableTheme === 'premium' 
                  ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)'
                  : 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
                border: tableTheme === 'premium' 
                  ? '2px solid #8B5CF6' 
                  : '2px solid #333',
                borderRadius: '16px',
                color: tableTheme === 'premium' ? '#A78BFA' : '#888',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
              }}
            >
              <span style={{ marginBottom: '10px', display: 'flex' }}><Crown size={36} /></span>
              <span style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>Premium</span>
              <span style={{ fontSize: '12px', opacity: 0.7 }}>Premium table with dealer</span>
            </button>
          </div>
          
          {/* Dealer selection for premium theme */}
          {tableTheme === 'premium' && (
            <div style={{
              marginTop: '20px',
              paddingTop: '20px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
              <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '12px' }}>
                Select your table dealer:
              </p>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setDealerImageUrl('/images/dealers/dealer-female-1.png')}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '12px',
                    background: dealerImageUrl === '/images/dealers/dealer-female-1.png'
                      ? 'rgba(139, 92, 246, 0.15)'
                      : 'rgba(255, 255, 255, 0.05)',
                    border: dealerImageUrl === '/images/dealers/dealer-female-1.png'
                      ? '2px solid #8b5cf6'
                      : '2px solid #333',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{
                    width: '80px',
                    height: '100px',
                    background: 'linear-gradient(135deg, #1e293b, #0f172a)',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    overflow: 'hidden',
                  }}>
                    <img
                      src="/images/dealers/dealer-female-1.png"
                      alt="Sofia the dealer"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                    />
                  </div>
                  <span style={{ color: '#a78bfa', fontSize: '13px', fontWeight: 600 }}>Sofia</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ============================================================================ */}
        {/* CREATE BUTTON - Tutorial Step 8 (Final) */}
        {/* ============================================================================ */}
        <div className="create-actions" data-tutorial="create-button">
          <button 
            className="cancel-btn"
            onClick={() => navigate('/tables')}
            disabled={creating}
          >
            Cancel
          </button>
          <button 
            className="create-btn"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? '⏳ Creating...' : '🃏 Create Table'}
          </button>
        </div>

        {/* Info Footer */}
        <div className="info-footer">
          <p>
            <strong>Note:</strong> Special rules are set when the table is created and cannot be changed during the game.
            Players joining your table will see which rules are active before they sit down.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateTablePage;